import { apiFetch } from './client';

/**
 * `work.*` — the Jira and GitHub view.
 *
 * Every response from `/api/work/trends` carries `computed_at_ms` and
 * `age_ms` because the view is a **stored snapshot**, not a live query:
 * `work.trends.snapshot` assembles each window on a timer. A page that
 * renders these numbers without saying how old they are invites someone
 * to read an hour-old figure as current, so `age_ms` is part of the
 * contract rather than a debugging aid.
 */

export type DwellTier = 'cold' | 'warm' | 'hot';
/**
 * A lane's throughput against its previous window.
 *
 * **`decline` was missing from this union until 2026-09-27 and the route
 * has always returned it** — `work/trends/health.py` answers `overload`,
 * `decline`, `watch` or `healthy`, and wingd's four lanes were live with
 * exactly one of each. `stalled` was here and the backend never produces
 * it. Both corrected against the source rather than against a sample,
 * because a sample only shows what happened to occur.
 */
export type ThroughputHealth =
  | 'healthy'
  | 'watch'
  | 'overload'
  | 'decline';
export type WipTier = 'light' | 'normal' | 'over';

/**
 * A lane's category — an arbitrary string, not a closed set.
 *
 * **Which lanes exist is deployment configuration**, read from
 * `work.trends.lanes`, so this cannot be a union. wingd configures `td`,
 * `sup`, `proj` and `core`; the predecessor's diagram hardcoded exactly
 * those four and would have drawn nothing for a deployment that
 * configured others. The diagram takes its lane order and colours from
 * `view.lanes`, which the route returns in configured order and with each
 * lane's own colour.
 */
export type LaneCategory = string;

/**
 * `fresh` / `mid` / `stale` bucket counts for one state.
 *
 * A named alias over the API's `Record<string, number>` so the diagram's
 * `AgeStrip` can state which three keys it reads. The route does not
 * promise only those three, which is why the underlying type stays open.
 */
export interface AgeDistribution {
  fresh: number;
  mid: number;
  stale: number;
}

/**
 * One state's mean dwell, as the cycle-time strip wants it.
 *
 * The API answers `mean_cycle_days` as an object keyed by state name, and
 * the strip needs an ordered list — order is the workflow, which an object
 * does not carry. `cycleTimeEntries` below does the conversion against the
 * lane's own `states` array, which is the only thing that knows the order.
 */
export interface CycleTimeEntry {
  state: string;
  mean_days: number;
}

export interface Assignee {
  name: string;
  count: number;
  native_lane: string | null;
}

export interface StateCell {
  name: string;
  count: number;
  /** `null` when nothing in this state has a measurable dwell. */
  mean_dwell_days: number | null;
  /** `null` alongside a null dwell — "not measured", not "cold". */
  dwell_tier: DwellTier | null;
  transitions_in: number;
  /** `fresh` / `mid` / `stale` bucket counts. */
  age_distribution: Record<string, number>;
  assignees: Assignee[];
  rework_count: number;
}

export interface Throughput {
  daily: Array<Record<string, number>>;
  previous_daily: Array<Record<string, number>>;
  window_total: number;
  previous_total: number;
  /** `null` when the previous window was empty — no denominator. */
  pct_change: number | null;
  health: ThroughputHealth;
}

export interface DriftedMember {
  name: string;
  from_lane: string;
  days_sustained: number;
}

export interface HandoffLatency {
  from_state: string;
  to_state: string;
  /** `null` when no hand-off in this pair had a measurable gap. */
  median_hours: number | null;
  samples: number;
}

export interface LaneCount {
  lane: string;
  count: number;
  share: number;
}

export interface Flow {
  total: number;
  delta_vs_previous: number;
  by_lane: LaneCount[];
}

export interface LaneView {
  category: string;
  label: string;
  color: string;
  projects: string[];
  native_members: string[];
  states: StateCell[];
  throughput: Throughput;
  /** State name to mean days spent in it, including the initial sojourn. */
  mean_cycle_days: Record<string, number | null>;
  handoff_latencies: HandoffLatency[];
  drifted_members: DriftedMember[];
}

/**
 * `mean_cycle_days` as an ordered list, following the lane's state order.
 *
 * **A null mean becomes 0, and that is a real loss of meaning.** The route
 * returns null for a state where nothing had a measurable dwell, which is
 * "not measured" rather than "instant" — but the strip's job is to show
 * every state in the workflow, and dropping the state would take it out of
 * the diagram entirely. Zero with the state still visible is the lesser
 * distortion; the strip already gives a zero-dwell state its minimum width
 * rather than collapsing it.
 */
export function cycleTimeEntries(lane: LaneView): CycleTimeEntry[] {
  return lane.states.map((state) => ({
    state: state.name,
    mean_days: lane.mean_cycle_days[state.name] ?? 0,
  }));
}

export interface LaneShare {
  lane: string;
  share: number;
}

export interface MemberWip {
  current: number;
  baseline: number;
  tier: WipTier;
}

export interface MemberEffort {
  person: string;
  native_lane: string | null;
  split: LaneShare[];
  jira_actions: number;
  commits: number;
  wip: MemberWip | null;
}

export interface Effort {
  members: MemberEffort[];
  /**
   * Actors the roster could not resolve, keyed by source — a Jira display
   * name and a GitHub login are different strings for the same person and
   * frequently neither is the canonical one. Surfaced rather than dropped:
   * an unmatched actor is effort the split does not account for, so a
   * page that hides them shows a total that does not add up.
   */
  unmatched: Record<string, string[]>;
}

export interface Drift {
  person: string;
  from_lane: string;
  to_lane: string;
  days_sustained: number;
}

/**
 * An anomalous week on one series.
 *
 * `week_index` indexes `Patterns.weeks`, and `metric` names the series it
 * occurred in. **Nothing says which direction it went** — the route reports
 * the point, not the sign — so a reader marks the week and looks, rather
 * than being told it got worse.
 */
export interface PatternAnomaly {
  week_index: number;
  metric: string;
}

/** Eight weeks of one lane. Every series is the same length as `weeks`. */
export interface LanePattern {
  category: string;
  throughput: number[];
  cycle_time_days: number[];
  backlog_size: number[];
  /** Arrivals minus departures, so the sign carries the message. */
  weekly_balance: number[];
  anomalies: PatternAnomaly[];
}

/**
 * The weekly pattern behind the window.
 *
 * **Computed by `work.trends.snapshot` and rendered nowhere until
 * 2026-09-27.** It was typed `Record<string, unknown>` here, which is the
 * shape of a field nobody reads — and it stayed unread through four
 * releases of "the Insights tab is ported".
 */
export interface Patterns {
  /** Week start timestamps, oldest first. */
  weeks: number[];
  lanes: LanePattern[];
}

export interface TrendsView {
  window_days: number;
  input: Flow;
  outcome: Flow;
  lanes: LaneView[];
  effort: Effort;
  drift: Drift[];
  patterns: Patterns | null;
  intent_mix: Record<string, number>;
  headline: string;
}

export interface TrendsResponse {
  window_days: number;
  computed_at_ms: number;
  age_ms: number;
  windows: number[];
  view: TrendsView;
}

export interface CollectorRun {
  started_at_ms: number;
  duration_ms: number;
  captured: number;
  inserted: number;
  updated: number;
  skipped: number;
  /** A JSON string of the errors the pass reported, or null. */
  errors: string | null;
}

export interface CollectorRow {
  name: string;
  /**
   * Whether `work.collect` registered it this boot. The route returns the
   * union of registered collectors and ones that have ever run, so a
   * `registered: false` row is one that used to run and no longer does —
   * which is a fact worth showing rather than an absence.
   */
  registered: boolean;
  run_count: number;
  error_count: number;
  last_success_at_ms: number | null;
  last_run: CollectorRun | null;
  recent_runs: CollectorRun[];
}

export interface Observation {
  id: number;
  fingerprint: string;
  subject: string;
  evidence: unknown;
  status: string;
  first_seen_at_ms: number | null;
  last_seen_at_ms: number | null;
  resolved_at_ms: number | null;
  interpreter_name: string | null;
  interpreter_version: number | null;
  pattern: string | null;
  metric_name: string | null;
  /**
   * `null` here can mean "not measured" *or* "a non-finite float the
   * route refused to serialise" — it coerces inf/NaN to null and logs
   * the dropped path, because `JSONResponse` renders with
   * `allow_nan=False` and would otherwise 500 on the whole response.
   */
  value: number | null;
  threshold: number | null;
  params: unknown;
  last_value: number | null;
}

/** One window. Omit `window` for the smallest stored one. */
export const fetchTrends = (window?: number): Promise<TrendsResponse> =>
  apiFetch<TrendsResponse>(
    window === undefined
      ? '/api/work/trends'
      : `/api/work/trends?window=${window}`,
  );

export const fetchTrendsWindows = (): Promise<{ windows: number[] }> =>
  apiFetch<{ windows: number[] }>('/api/work/trends/windows');

export const fetchCollectors = (): Promise<{ collectors: CollectorRow[] }> =>
  apiFetch<{ collectors: CollectorRow[] }>('/api/work/collectors');

export const fetchObservations = (
  status: 'open' | 'resolved' | 'all' = 'open',
): Promise<{ observations: Observation[]; limit: number }> =>
  apiFetch<{ observations: Observation[]; limit: number }>(
    `/api/work/observations?status=${status}`,
  );

/* -- workload ------------------------------------------------------- */

export interface MetricCell {
  value: number | null;
  /**
   * Why there is no value. `null` alongside a `null` value means the
   * metric computed and genuinely had nothing to say; a string is the
   * metric declining and saying why — "no hardware labels configured"
   * is a configuration gap, "no commits in window" is a fact about the
   * person, and a bare null cannot tell them apart.
   */
  reason: string | null;
  breakdown: unknown;
}

export interface WorkloadMember {
  /**
   * The person's home lane, from `work.trends`.
   *
   * `null` when `work.trends` is absent or does not place them. The
   * predecessor grouped its grid by lane and this route returned no lane at
   * all, so the port was a flat table — recorded in the audit as one of the
   * four free gaps.
   */
  native_lane: string | null;
  person: string;
  /** Keyed by metric name. Every dimension is present, even when null. */
  metrics: Record<string, MetricCell>;
}

export interface WorkloadResponse {
  /** How many members got a lane. 0 means the grid renders flat. */
  lanes_resolved: number;
  computed_at_ms: number;
  age_ms: number;
  /** The dimensions, in the order the route reports them. */
  dimensions: string[];
  members: WorkloadMember[];
}

export const fetchWorkload = (): Promise<WorkloadResponse> =>
  apiFetch<WorkloadResponse>('/api/work/workload');

/** One ticket behind a planning failure, as `unplanned_tasks_count` stores it. */
export interface UnplannedTicket {
  issue_key: string;
  project: string;
  created_at_ms: number;
  due_at_ms: number;
}

/**
 * `unplanned_tasks_count`'s breakdown: tickets created after a planning cycle
 * began and due before the next one.
 *
 * `by_project` is counted from every unplanned ticket; `tickets` is capped at
 * 30. Segments come from the first, the evidence list from the second, and a
 * row whose `count` exceeds `tickets.length` says so rather than showing a
 * list that silently stops.
 *
 * `undated` counts the tickets the rule cannot judge — no due date, so no
 * "due before the next cycle". On the deployment this was written against,
 * 45-60% of support and delivery tickets carry none, which is why a zero on
 * this panel is shown beside it.
 */
export interface UnplannedBreakdown {
  count: number;
  window_days: number;
  projects: string[];
  by_project: Record<string, number>;
  undated: number;
  tickets: UnplannedTicket[];
}

/**
 * The breakdown, if this cell carries one of the expected shape.
 *
 * `MetricCell.breakdown` is `unknown` because every metric stores its own
 * shape, and nothing in the type system connects that shape to the Python
 * that writes it. Checked rather than cast: a key renamed on the other side
 * would otherwise read as zero in every segment, and a panel of zeroes here
 * looks like a team that plans perfectly.
 */
export function unplannedOf(cell: MetricCell | undefined): UnplannedBreakdown | null {
  const b = cell?.breakdown;
  if (typeof b !== 'object' || b === null) return null;
  const r = b as Record<string, unknown>;
  if (
    typeof r.count !== 'number' ||
    typeof r.window_days !== 'number' ||
    !Array.isArray(r.projects) ||
    typeof r.by_project !== 'object' ||
    r.by_project === null ||
    !Array.isArray(r.tickets)
  ) {
    return null;
  }
  return {
    count: r.count,
    window_days: r.window_days,
    projects: r.projects.map(String),
    by_project: r.by_project as Record<string, number>,
    undated: typeof r.undated === 'number' ? r.undated : 0,
    tickets: r.tickets as UnplannedTicket[],
  };
}

/* -- people --------------------------------------------------------- */

export interface PersonSummary {
  subject: string;
  name: string;
  latest_activity_ms: number | null;
  metric_value_count: number;
  open_observation_count: number;
}

export interface SeriesPoint {
  ts_ms: number;
  /** `null` is a gap, never interpolated. See `PersonDetail`. */
  value: number | null;
}

export interface MetricSeries {
  name: string;
  /** Ascending, one point per metric run. Gaps are gaps. */
  points: SeriesPoint[];
  latest_value: number | null;
  /** Why the newest point has no value, when it has none. */
  latest_reason: string | null;
}

export interface PersonObservation {
  id: number;
  metric_name: string | null;
  pattern: string | null;
  status: string;
  evidence: unknown;
  first_seen_at_ms: number | null;
  last_seen_at_ms: number | null;
}

export interface PersonRepository {
  repo: string;
  commits: number;
  /** Absent when the deployment configured no taxonomy. */
  layer?: string;
  layer_label?: string;
  hardware_design?: boolean;
}

export interface PersonDetail {
  name: string;
  subject: string;
  window_days: number;
  computed_at_ms: number;
  metrics: MetricSeries[];
  observations: PersonObservation[];
  /** Empty when `work.collect` is absent — context, not the substance. */
  repositories: PersonRepository[];
}

export const fetchPeople = (): Promise<{
  people: PersonSummary[];
  computed_at_ms: number;
}> => apiFetch('/api/work/people');

export const fetchPerson = (
  name: string,
  days?: number,
): Promise<PersonDetail> =>
  apiFetch<PersonDetail>(
    `/api/work/people/${encodeURIComponent(name)}${
      days === undefined ? '' : `?days=${days}`
    }`,
  );

/* -- metric catalogue ----------------------------------------------- */

export interface MetricCatalogEntry {
  name: string;
  version: number | null;
  /* -- from the registry (`MetricDefinition`). Null when the definition is
        gone, which means the metric was retired and its rows remain. -- */
  scope: string | null;
  unit: string | null;
  description: string | null;
  why_it_matters: string | null;
  data_sources: string[];
  /** Registered and deliberately not computed; `how_to_enable` says why. */
  unimplemented: boolean;
  how_to_enable: string | null;
  /** False means run history with no definition — a retired metric. */
  registered: boolean;
  /* -- from the run history (`metric_runs`) -- */
  run_count: number;
  last_run_at_ms: number | null;
  rows_written: number;
  failed_runs: number;
  mean_duration_ms: number | null;
  /**
   * Registered and never computed once.
   *
   * Distinct from `unimplemented` (nobody declined it) and from `stale`
   * (it did not fall behind, it never started).
   */
  never_ran: boolean;
  /**
   * It did not run in the most recent pass.
   *
   * The runner writes every metric at one timestamp per pass, so a metric
   * whose last run predates the newest run in the table did not
   * participate. On wingd that is true of `dependency_blocking_count`,
   * 170 days behind the rest — a metric in the catalogue computing
   * nothing, which no other view can show because "no rows" and "no
   * subjects" look identical.
   */
  stale: boolean;
}

export interface MetricCatalog {
  metrics: MetricCatalogEntry[];
  latest_pass_ms: number | null;
  /** How many definitions the registry held. 0 means it was unreachable. */
  registry_size: number;
  computed_at_ms: number;
}

export interface MetricPoint {
  ts_ms: number;
  value: number | null;
  reason: string | null;
}

export interface MetricSeriesResponse {
  name: string;
  scope: string | null;
  /** True when the points are a mean across scopes rather than one subject. */
  aggregate: boolean;
  window_days: number;
  points: MetricPoint[];
  truncated: boolean;
  scopes: string[];
  scopes_truncated: boolean;
  computed_at_ms: number;
}

/** The catalogue costs ~300ms against a 2GB store. Never poll it. */
export const fetchMetricCatalog = (): Promise<MetricCatalog> =>
  apiFetch<MetricCatalog>('/api/work/metrics');

export const fetchMetricSeries = (
  name: string,
  opts: { days?: number; scope?: string } = {},
): Promise<MetricSeriesResponse> => {
  const params = new URLSearchParams();
  if (opts.days) params.set('days', String(opts.days));
  if (opts.scope) params.set('scope', opts.scope);
  const qs = params.toString();
  return apiFetch<MetricSeriesResponse>(
    `/api/work/metrics/${encodeURIComponent(name)}/series${qs ? `?${qs}` : ''}`,
  );
};

/* -- pulse: the narrative and its findings -------------------------- */

/**
 * One finding from a `work.pulse` run.
 *
 * Four of the predecessor's Insights elements read this and none survived
 * the port: the narrative block, the suggested checks, the chips on the
 * pipeline diagram, and the side panel. 2,364 of these migrated from the
 * predecessor and were unreachable until `/api/work/pulse/latest` existed.
 */
export interface PulseFinding {
  id: number | null;
  run_id: string;
  generated_at_ms: number;
  window_days: number;
  /** The lane this belongs to — places the chip on the diagram. */
  lane_primary: string;
  lane_secondary: string | null;
  kind: string;
  severity: string;
  /** 0–1. The predecessor placed chips at `>= 0.5` and said so. */
  confidence: number;
  headline: string;
  narrative: string;
  suggested_check: string | null;
  /** Parsed by the route; `[]` when the stored JSON would not parse. */
  evidence: unknown;
  tool_calls: unknown;
  reasoning: string | null;
}

export interface PulseRun {
  run_id: string;
  window_days: number;
  started_at_ms: number;
  completed_at_ms: number | null;
  trigger: string | null;
  run_type: string | null;
  /** `null` until the narrative has been composed for this run. */
  narrative_text: string | null;
  narrative_composed_at_ms: number | null;
  finding_count: number;
}

export interface PulseLatest {
  window_days: number;
  /** `null` on a window with no run yet — an empty state, not an error. */
  run: PulseRun | null;
  findings: PulseFinding[];
  computed_at_ms: number;
}

export interface CachedBriefing {
  perspective: string;
  kind: string;
  generated_at_ms: number;
  /** Already-parsed content; shape varies by `kind`. */
  content: unknown;
}

/** One request, so a narrative can never be shown beside another run's findings. */
export const fetchPulseLatest = (window?: number): Promise<PulseLatest> =>
  apiFetch<PulseLatest>(
    window === undefined
      ? '/api/work/pulse/latest'
      : `/api/work/pulse/latest?window=${window}`,
  );

export const fetchBriefings = (): Promise<{
  briefings: CachedBriefing[];
  computed_at_ms: number;
}> => apiFetch('/api/work/pulse/briefings');

/* -- workload detail: the numbers for the bars, records on demand ---- */

/**
 * True counts for one member over the window.
 *
 * **Not derived from the record lists.** A stacked bar's length *is* the
 * count and the record readers are capped at 200 — on the live mirror one
 * member's 30-day commits are 315 and their Jira actions 668, so a bar
 * drawn from the capped lists understates by more than threefold and looks
 * entirely plausible. Counts and records are deliberately different
 * queries.
 */
export interface MemberCounts {
  open: number;
  resolved: number;
  /** Tickets this person created. Most visible on a tech lead's row. */
  reported: number;
  commits: number;
  prs_merged: number;
  /** Over 24h open-to-merge — shipping that survives cadence noise. */
  prs_merged_deep: number;
  prs_reviewed: number;
  /** Execution on their own tickets. */
  actions_own: number;
  /** Coordination on someone else's. One undifferentiated number before. */
  actions_other: number;
  comments_own: number;
  comments_other: number;
}

export interface WorkloadDetailMember {
  person: string;
  role: string | null;
  /**
   * The home lane `work.trends` assigns, or `null` when it does not place
   * this person. The heatmap outlines this cell and colours the rest as
   * drift.
   */
  native_lane?: string | null;
  jira_aliases: string[];
  github_aliases: string[];
  /** `null` when this member's read failed; the row is kept, not dropped. */
  counts: MemberCounts | null;
  error?: boolean;
  open_by_lane?: Record<string, number>;
  /**
   * Open tickets in a project no lane claims.
   *
   * Surfaced rather than folded into a lane or dropped: folding
   * misattributes the work and dropping makes the bars add up to less than
   * the person's open total, silently.
   */
  open_unmapped?: number;
  open_by_project?: Array<{ project: string; count: number }>;
  /**
   * `{lane: {stage label: count}}` over the same window as `counts`.
   *
   * Every configured stage is present, including at zero — a stage a member
   * never entered and a stage missing from the response look identical to a
   * reader but are not the same fact, and a missing key would shorten one
   * row's segment list so the colours stopped lining up down the panel.
   *
   * `{}` when no lane declares stages, or when this member's read failed.
   */
  stage_counts?: Record<string, Record<string, number>>;
}

/**
 * One named step in a lane's pipeline, from `work.metrics.workflow.stages`.
 *
 * **`statuses` is a list because one step has several spellings.** The
 * support project records 165 transitions to `IN PROGRESS` and 80 to
 * `In Progress` — the same step, entered through different screens over the
 * years — so a stage naming one of them counts a third of the work.
 *
 * `color` is optional; a stage without one is drawn from a ramp off the
 * lane's own colour.
 */
export interface StageDef {
  label: string;
  statuses: string[];
  color: string | null;
}

export interface WorkloadLane {
  category: string;
  label: string;
  color: string;
}

export interface WorkloadDetail {
  window_days: number;
  /**
   * The configured lanes in order, with their label and colour — `work.trends`'
   * parse of `work.trends.lanes`. Optional for a server older than extensions
   * 0.44.0, where every lane then draws neutral under its raw key.
   */
  lanes?: WorkloadLane[];
  record_cap: number;
  lanes_configured: number;
  /**
   * The statuses "open" excludes — `status_sets.engineer_terminal`. Shown by
   * the views, because "open" used to mean "no Jira resolution", and on a
   * workflow that closes without one that counted finished work as load.
   */
  open_excludes?: string[];
  /**
   * `{project: lane}` from `work.trends.lanes`, so anything keyed by project
   * is coloured by its lane — the same colour for the same lane on every
   * panel.
   */
  project_lanes?: Record<string, string>;
  /**
   * `{lane: stages}` in configured order.
   *
   * The page draws its segments from these rather than from a member's row:
   * deriving the order from whichever member came first would reorder the
   * bars as the roster changed.
   *
   * `{}` when nothing is configured, which the page reads as "draw no
   * pipeline panels" — not as a panel of zeroes, which is what the
   * predecessor showed any deployment whose statuses were not its own.
   */
  stages?: Record<string, StageDef[]>;
  members: WorkloadDetailMember[];
  computed_at_ms: number;
}

export interface MemberRecords {
  person: string;
  role: string | null;
  window_days: number;
  record_cap: number;
  /** Which lists hit the cap, so a reader is told rather than assuming. */
  truncated: string[];
  /**
   * Per kind. `transitions` is present only when stages are configured, and
   * each of its rows carries its own `project` and `to_status` so the page
   * groups them into stages using the definitions it already holds.
   */
  records: Record<string, Array<Record<string, unknown>>>;
  computed_at_ms: number;
}

/** A row of `records.transitions`. */
export interface TransitionRecord {
  key: string;
  to_status: string;
  changed_at_ms: number;
  project: string | null;
}

/** Counts for every member. ~48ms for eleven on the live mirror. */
export const fetchWorkloadDetail = (days?: number): Promise<WorkloadDetail> =>
  apiFetch<WorkloadDetail>(
    days === undefined
      ? '/api/work/workload/detail'
      : `/api/work/workload/detail?days=${days}`,
  );

/** One member's evidence, fetched when a reader asks for it. */
export const fetchMemberRecords = (
  person: string,
  days?: number,
): Promise<MemberRecords> =>
  apiFetch<MemberRecords>(
    `/api/work/workload/detail/${encodeURIComponent(person)}` +
      (days === undefined ? '' : `?days=${days}`),
  );
